import posthog from 'posthog-js';

export const UTM_STORAGE_KEY = 'ph_utm';
export const UTM_TTL_MS = 30 * 60 * 1000;
export const SESSION_SOURCE_KEY = 'session_source';

export const CAMPAIGN_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'placement',
  'automation_id'
] as const;

export type CampaignParam = (typeof CAMPAIGN_PARAMS)[number];

export type CampaignAttribution = { utm_source: string } & Partial<
  Record<Exclude<CampaignParam, 'utm_source'>, string>
>;

export type StoredCampaign = CampaignAttribution & { expires: number };

const MAX_LENGTH = 100;

// Ids keep their case; channel names are lowercased so they group in PostHog.
const CASE_SENSITIVE: CampaignParam[] = ['utm_content', 'automation_id'];

const normalize = (value: unknown, key: CampaignParam): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  const cased = CASE_SENSITIVE.includes(key) ? trimmed : trimmed.toLowerCase();
  return cased.slice(0, MAX_LENGTH) || undefined;
};

export const campaignFromParams = (
  get: (name: CampaignParam) => unknown
): CampaignAttribution | null => {
  const utm_source = normalize(get('utm_source'), 'utm_source');
  if (!utm_source) return null;
  const entry: CampaignAttribution = { utm_source };
  for (const key of CAMPAIGN_PARAMS) {
    if (key === 'utm_source') continue;
    const value = normalize(get(key), key);
    if (value) entry[key] = value;
  }
  return entry;
};

export const withExpiry = (entry: CampaignAttribution): StoredCampaign => ({
  ...entry,
  expires: Date.now() + UTM_TTL_MS
});

export const storeCampaign = (entry: CampaignAttribution): void => {
  try {
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(withExpiry(entry)));
  } catch {}
};

export const readCampaign = (): CampaignAttribution | null => {
  try {
    const raw = localStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const valid =
      typeof parsed.expires === 'number'
        ? Date.now() <= parsed.expires
        : typeof parsed.ts === 'number'
          ? Date.now() - parsed.ts < UTM_TTL_MS
          : false;
    const entry = valid ? campaignFromParams((key) => parsed[key]) : null;
    if (entry) storeCampaign(entry);
    else localStorage.removeItem(UTM_STORAGE_KEY);
    return entry;
  } catch {
    return null;
  }
};

export const registerCampaign = (entry: CampaignAttribution | null): void => {
  const present: Partial<Record<CampaignParam, string>> = {};
  for (const key of CAMPAIGN_PARAMS) {
    const value = entry?.[key];
    if (value) present[key] = value;
    else posthog.unregister(key);
  }
  if (Object.keys(present).length) posthog.register(present);
};

export const ensureSessionSource = (
  preferred: string | null | undefined
): string | null => {
  try {
    const existing = sessionStorage.getItem(SESSION_SOURCE_KEY);
    const sessionSource = existing || preferred || null;
    if (!sessionSource) return null;
    if (!existing) sessionStorage.setItem(SESSION_SOURCE_KEY, sessionSource);
    posthog.register({ session_source: sessionSource });
    return sessionSource;
  } catch {
    return null;
  }
};
